/**
 * Priority / Work queue tab (WO-P1).
 *
 * getWorkQueue() → a server-ranked list of mixed entries (tasks, punchlist items,
 * schedule events). Each row shows a kind badge, the title, a priority marker, and a
 * relative due (overdue highlighted). Pull-to-refresh re-fetches. Tapping routes by
 * kind:
 *   'task'           → /(app)/tasks/[id]
 *   'schedule_event' → /(app)/schedule/[id]
 *   'punchlist_item' → an inline expandable detail (no standalone punchlist screen yet)
 *
 * Empty state: "All clear". Never crashes on loading/empty/error. NEVER imports
 * @smartbox/*. No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { getWorkQueue } from '../../src/api/tasks';
import {
  Screen,
  Text,
  Card,
  Button,
  Badge,
  ListRow,
  queueKindBadge,
  priorityBadge,
} from '../../src/components/ui';
import { formatDueRelative, extractErrorMessage, humanize } from '../../src/lib/format';
import { theme } from '../../src/theme';
import { useHelpEntry } from '../../src/help';
import { SCREEN_KEYS } from '../../src/help';
import type { WorkQueueEntry } from '@beam/api-types';

function QueueRow({
  entry,
  expanded,
  onPress,
}: {
  entry: WorkQueueEntry;
  expanded: boolean;
  onPress: () => void;
}) {
  const kind = queueKindBadge(entry.kind);
  const pri = priorityBadge(entry.priority);
  const due = formatDueRelative(entry.due_at);

  return (
    <View style={styles.entryWrap}>
      <ListRow
        title={entry.title}
        subtitle={due.text}
        left={<Badge status={kind.key} label={kind.label} />}
        right={<Badge status={pri.key} label={pri.label} />}
        onPress={onPress}
        style={due.overdue ? styles.overdueRow : undefined}
      />
      {expanded && entry.kind === 'punchlist_item' ? (
        <Card>
          <Text variant="bodyStrong">{entry.title}</Text>
          <Text variant="small" color="secondary">
            Punchlist item · {humanize(entry.status)}
          </Text>
          <Text variant="small" color={due.overdue ? 'danger' : 'secondary'}>
            {due.text}
          </Text>
          {entry.job_id ? (
            <Text variant="data" color="secondary">
              Job {entry.job_id.slice(0, 8).toUpperCase()}
            </Text>
          ) : null}
        </Card>
      ) : null}
    </View>
  );
}

export default function QueueScreen() {
  const emptyHelp = useHelpEntry(SCREEN_KEYS.queue, 'empty');
  const router = useRouter();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: ['work-queue'],
    queryFn: getWorkQueue,
    staleTime: 15_000,
  });

  const onEntryPress = (entry: WorkQueueEntry) => {
    switch (entry.kind) {
      case 'task':
        router.push(`/(app)/tasks/${entry.id}`);
        break;
      case 'schedule_event':
        router.push(`/(app)/schedule/${entry.id}`);
        break;
      case 'punchlist_item':
        setExpandedId((cur) => (cur === entry.id ? null : entry.id));
        break;
    }
  };

  // Loading (first load only — pull-to-refresh uses the RefreshControl spinner).
  if (isLoading) {
    return (
      <>
        <Stack.Screen options={{ title: 'Priority Queue' }} />
        <Screen>
          <View style={styles.centeredState}>
            <ActivityIndicator
              size="large"
              color={theme.color.action.primary}
              accessibilityLabel="Loading work queue"
            />
          </View>
        </Screen>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: 'Priority Queue' }} />
      <Screen padded={false}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={() => refetch()}
            tintColor={theme.color.action.primary}
          />
        }
      >
        {isError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error, 'Failed to load the work queue.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
            />
          </Card>
        ) : !data || data.length === 0 ? (
          <View style={styles.centeredState}>
            <Text variant="title">All clear</Text>
            <Text variant="body" color="secondary" style={styles.centeredText}>
              {emptyHelp?.body ?? 'Nothing needs your attention right now. Pull down to refresh.'}
            </Text>
          </View>
        ) : (
          <View style={styles.list}>
            <Text variant="label" color="secondary">
              {data.length} {data.length === 1 ? 'item' : 'items'}
            </Text>
            {data.map((entry) => (
              <QueueRow
                key={`${entry.kind}:${entry.id}`}
                entry={entry}
                expanded={expandedId === entry.id}
                onPress={() => onEntryPress(entry)}
              />
            ))}
          </View>
        )}
      </ScrollView>
      </Screen>
    </>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingVertical: theme.spacing['4xl'],
    paddingHorizontal: theme.spacing['2xl'],
  },
  centeredText: { textAlign: 'center' },
  scrollContent: {
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.xl,
    gap: theme.spacing.lg,
    flexGrow: 1,
  },
  list: { gap: theme.spacing.sm },
  entryWrap: { gap: theme.spacing.sm },
  overdueRow: { borderColor: theme.color.action.danger },
});
